/**
 * Dashboard → Data Hygiene: the shapes the API sends and the pure rules behind the
 * screen. No React in here.
 *
 * The dashboard lists what is wrong in the records this team enters itself: stores
 * entered twice, stores and people with a value missing, and values that cannot be
 * right. The API decides what is wrong, and which rows a "Mark as OK" keeps off the
 * lists. This file only names the checks, words the counts, filters the rows on screen
 * and builds the links, the mark requests and the Excel sheet.
 *
 * The types are the web's copy of the API contract
 * (GET /data-hygiene/:projectId/stores and /employees). Keep the two in step.
 */
import type { TableCell, TableSheet } from '../../utils/tableXlsx';

// ── The contract ─────────────────────────────────────────────────────────────

/** 'unknown' = the check could not be run. It is never shown as 0. */
export type HygieneCheckStatus = 'ready' | 'unknown';

export type StoreCheckId =
  | 'same-name' | 'same-coordinates' | 'same-store-code'
  | 'fields-missing' | 'extra-field-missing' | 'staffing-status-not-set'
  | 'coordinates-wrong' | 'state-city' | 'status-not-recognised' | 'closed-with-people';

export interface HygieneStoreRow {
  storeId: string;
  storeName: string;
  /** '' when blank. */
  storeCode: string;
  /** Category name, '' when none. */
  category: string;
  city: string;
  state: string;
  /** store_status as stored, '' when blank. */
  status: string;
  /** Duplicate checks: rows with one groupKey belong together. */
  groupKey?: string;
  /** Duplicate checks: what the block is headed with (the shared name, point or code). */
  groupLabel?: string;
  /** same-name only: at least two stores of the group are in one city. */
  sameCity?: boolean;
  /** Missing checks: labels of what is missing, most important first. */
  missing?: string[];
  /** "Looks wrong" checks: a few plain words saying what is wrong. */
  problem?: string;
  /** closed-with-people only. */
  people?: Array<{ employeeCode: string; employeeName: string }>;
}

export interface HygieneFieldCount { label: string; count: number }

export interface HygieneStoreCheck {
  id: StoreCheckId;
  status: HygieneCheckStatus;
  /** Stores listed (rows.length); null when status is 'unknown'. */
  count: number | null;
  /** Duplicate checks: how many shared names / points / codes. */
  groups?: number;
  /** fields-missing, extra-field-missing: stores per label. */
  fieldCounts?: HygieneFieldCount[];
  rows: HygieneStoreRow[];
}

/** A store row a "Mark as OK" keeps off its check's list. */
export interface HygieneMarkedStore {
  check: StoreCheckId;
  /** Employee code of whoever marked it. */
  markedBy: string;
  /** ISO time. */
  markedAt: string;
  row: HygieneStoreRow;
}

export interface DataHygieneStores {
  /** ISO time the checks ran. */
  asOf: string;
  /** Open stores of the project. */
  storesChecked: number;
  /** Distinct stores in any 'ready' check, marked rows left out. */
  storesWithIssues: number;
  /** Always all ten, in the order of StoreCheckId. Marked rows are not in them. */
  checks: HygieneStoreCheck[];
  /** The rows marks keep off the lists, newest first. */
  marked: HygieneMarkedStore[];
}

export type EmployeeCheckId = 'two-stores' | 'two-roles' | 'no-role' | 'extra-field-missing';

export interface HygieneEmployeeRow {
  employeeCode: string;
  /** '' when not known. */
  employeeName: string;
  /** The person's role names in this project joined with ', '; '' when none. */
  roleName: string;
  /** two-stores. */
  stores?: Array<{ storeId: string; storeName: string; storeCode: string }>;
  /** two-roles. */
  roles?: string[];
  /** extra-field-missing: the field labels. */
  missing?: string[];
}

export interface HygieneEmployeeCheck {
  id: EmployeeCheckId;
  status: HygieneCheckStatus;
  count: number | null;
  fieldCounts?: HygieneFieldCount[];
  rows: HygieneEmployeeRow[];
}

/** A person row a "Mark as OK" keeps off its check's list. */
export interface HygieneMarkedEmployee {
  check: EmployeeCheckId;
  markedBy: string;
  markedAt: string;
  row: HygieneEmployeeRow;
}

export interface DataHygieneEmployees {
  asOf: string;
  /** Active people on the project's roster; null when it could not be read. */
  peopleChecked: number | null;
  /** Distinct people in any 'ready' check, marked rows left out. */
  peopleWithIssues: number;
  /** Always all four, in the order of EmployeeCheckId. Marked rows are not in them. */
  checks: HygieneEmployeeCheck[];
  /** The rows marks keep off the lists, newest first. */
  marked: HygieneMarkedEmployee[];
}

// ── The checks, as the screen names them ─────────────────────────────────────

export type HygieneSide = 'stores' | 'employees';

/** The two sides of the switch. `one` / `many` are what a row is called in a count. */
export const HYGIENE_SIDES: ReadonlyArray<{ id: HygieneSide; label: string; one: string; many: string }> = [
  { id: 'stores', label: 'Stores', one: 'store', many: 'stores' },
  { id: 'employees', label: 'Employees', one: 'person', many: 'people' },
];

export type HygieneGroupId = 'twice' | 'missing' | 'wrong';

/** The three boxes, left to right. Employees has no "Looks wrong" check, so no such box. */
export const HYGIENE_GROUPS: ReadonlyArray<{ id: HygieneGroupId; label: string }> = [
  { id: 'twice', label: 'Entered twice' },
  { id: 'missing', label: 'Missing' },
  { id: 'wrong', label: 'Looks wrong' },
];

interface CheckInfoBase {
  group: HygieneGroupId;
  /** Two to four words, drawn after the number. */
  label: string;
  /** What ?list= carries to open this check. A store check's slug is its id; an employee one is prefixed 'people-'. */
  slug: string;
  /** One plain sentence saying what the check lists. Shown on hover, never on the screen itself. */
  title: string;
}
export interface StoreCheckInfo extends CheckInfoBase { side: 'stores'; id: StoreCheckId }
export interface EmployeeCheckInfo extends CheckInfoBase { side: 'employees'; id: EmployeeCheckId }
export type HygieneCheckInfo = StoreCheckInfo | EmployeeCheckInfo;

const storeCheck = (id: StoreCheckId, group: HygieneGroupId, label: string, title: string): StoreCheckInfo =>
  ({ side: 'stores', id, slug: id, group, label, title });
const employeeCheck = (id: EmployeeCheckId, group: HygieneGroupId, label: string, title: string): EmployeeCheckInfo =>
  ({ side: 'employees', id, slug: `people-${id}`, group, label, title });

// The hover titles say what the API lists today (data-hygiene.logic). "Open" is a store
// whose status is not Inactive; every store check leaves Inactive stores out but two:
// Status, for an Inactive not written exactly 'Inactive', and the last one, which is
// about the people (who have not left) still posted at one. When a rule changes there,
// change its sentence here.

/** In the API's order, which is also the order on screen. */
export const STORE_CHECKS: readonly StoreCheckInfo[] = [
  storeCheck('same-name', 'twice', 'Same name', 'Open stores that have the same name as another open store.'),
  storeCheck('same-coordinates', 'twice', 'Same coordinates', 'Open stores that sit at the same coordinates as another open store.'),
  storeCheck('same-store-code', 'twice', 'Same store code', 'Open stores that have the same store code as another open store.'),
  storeCheck('fields-missing', 'missing', 'Fields missing', 'Open stores with coordinates, store code, category, state, city, trade, address or PIN code left blank, or with a PIN code that is not 6 digits.'),
  storeCheck('extra-field-missing', 'missing', 'Extra field missing', 'Open stores with a required extra field left blank.'),
  storeCheck('staffing-status-not-set', 'missing', 'Staffing status not set', 'Open stores whose staffing status has not been set.'),
  storeCheck('coordinates-wrong', 'wrong', 'Coordinates', 'Open stores whose coordinates cannot be right.'),
  storeCheck('state-city', 'wrong', 'State or city', 'Open stores whose state is not a state, or whose state and city look swapped.'),
  storeCheck('status-not-recognised', 'wrong', 'Status', 'Open stores whose status is blank, or is not exactly Active; and Inactive stores whose status is not written exactly Inactive.'),
  storeCheck('closed-with-people', 'wrong', 'Closed, people posted', 'Inactive stores that still have people posted at them who have not left: those people need moving.'),
];

export const EMPLOYEE_CHECKS: readonly EmployeeCheckInfo[] = [
  employeeCheck('two-stores', 'twice', 'At two stores', 'People posted at two or more open stores, unless their role allows several stores or they have left.'),
  employeeCheck('two-roles', 'twice', 'Two roles', 'People who hold two or more roles in this project.'),
  employeeCheck('no-role', 'missing', 'No role', 'Active people with no role in this project.'),
  employeeCheck('extra-field-missing', 'missing', 'Extra field missing', 'Active people with a required extra field left blank.'),
];

export const checksOf = (side: HygieneSide): readonly HygieneCheckInfo[] =>
  (side === 'stores' ? STORE_CHECKS : EMPLOYEE_CHECKS);

/** The check a ?list= value names; null for no value or one that is not a check. */
export function checkFromSlug(slug: string | null | undefined): HygieneCheckInfo | null {
  if (!slug) return null;
  const wanted = slug.trim().toLowerCase();
  return [...STORE_CHECKS, ...EMPLOYEE_CHECKS].find((c) => c.slug === wanted) ?? null;
}

/** Hover text of a check that could not be run. */
export const COULD_NOT_CHECK = 'Could not check just now';

// ── Marked OK ────────────────────────────────────────────────────────────────
// A row someone agreed with is marked OK from its Actions column and leaves its list.
// The rows marked OK are a list of their own, opened by ?list= like the checks.

/** What ?list= carries to open each side's "Marked OK" list. */
export const MARKED_SLUG: Readonly<Record<HygieneSide, string>> = { stores: 'marked-ok', employees: 'people-marked-ok' };

/** The side whose "Marked OK" list ?list= names; null when it names none. */
export function markedSideFromSlug(slug: string | null | undefined): HygieneSide | null {
  const wanted = String(slug ?? '').trim().toLowerCase();
  if (wanted === MARKED_SLUG.stores) return 'stores';
  if (wanted === MARKED_SLUG.employees) return 'employees';
  return null;
}

export const MARKED_LABEL = 'Marked OK';
/** The confirm, in the owner's words. */
export const MARK_QUESTION = 'Mark as OK?';
export const MARK_ANSWER = 'It will not be flagged again.';
/** Hover text of the mark control: the one rule a person needs. */
export const MARK_HOVER = 'Mark as OK. If what was flagged changes, it is flagged again.';

/** What the API is sent to mark (or unmark) one row of one check. */
export interface MarkRequest { side: HygieneSide; check: string; subject: string }

export const storeMarkRequest = (check: StoreCheckId, row: Pick<HygieneStoreRow, 'storeId'>): MarkRequest =>
  ({ side: 'stores', check, subject: row.storeId });
export const employeeMarkRequest = (check: EmployeeCheckId, row: Pick<HygieneEmployeeRow, 'employeeCode'>): MarkRequest =>
  ({ side: 'employees', check, subject: row.employeeCode });

/** '14 Oct 2026', for the Marked OK list; '' when it is not a time. */
export function fmtMarkedDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  return at.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' });
}

/** The words a failed mark or undo shows: the server's own sentence when it sent one. */
export function markErrorText(err: unknown, undo = false): string {
  const response = (err as { response?: { status?: number; data?: { message?: unknown } } })?.response;
  const message = response?.data?.message;
  if (typeof message === 'string' && message.trim() && response?.status && response.status < 500) return message;
  return undo ? 'Could not undo just now. Please try again.' : 'Could not mark it just now. Please try again.';
}

// ── Reading the API's answers ────────────────────────────────────────────────
// The screen draws every check, in its own order, whatever the answer holds. A check
// the answer leaves out (an older API) is "could not check", never a quiet 0.

interface AnyCheck<Id extends string, Row> {
  id: Id;
  status: HygieneCheckStatus;
  count: number | null;
  groups?: number;
  fieldCounts?: HygieneFieldCount[];
  rows: Row[];
}

function readChecks<Id extends string, Row>(raw: unknown, ids: readonly Id[]): Array<AnyCheck<Id, Row>> | null {
  if (!Array.isArray(raw)) return null;
  const sent = new Map<string, Partial<AnyCheck<Id, Row>>>();
  for (const check of raw) {
    if (check && typeof check === 'object' && typeof (check as { id?: unknown }).id === 'string') {
      sent.set((check as { id: string }).id, check as Partial<AnyCheck<Id, Row>>);
    }
  }
  return ids.map((id) => {
    const check = sent.get(id);
    if (!check || check.status !== 'ready') return { id, status: 'unknown' as const, count: null, rows: [] };
    // Only rows that are rows: one stray null would take the whole list down when it is drawn.
    const rows = Array.isArray(check.rows) ? check.rows.filter((row) => !!row && typeof row === 'object') : [];
    return {
      id,
      status: 'ready' as const,
      count: typeof check.count === 'number' && Number.isFinite(check.count) ? check.count : rows.length,
      groups: typeof check.groups === 'number' && Number.isFinite(check.groups) ? check.groups : undefined,
      fieldCounts: Array.isArray(check.fieldCounts) ? check.fieldCounts : undefined,
      rows,
    };
  });
}

const numberOr = <T,>(value: unknown, fallback: T): number | T =>
  (typeof value === 'number' && Number.isFinite(value) ? value : fallback);

/**
 * The marked rows of an answer: only entries naming a check of that side and holding a
 * row. An answer without them (an older API) has none.
 */
function readMarked<Id extends string, Row>(raw: unknown, ids: readonly Id[]): Array<{ check: Id; markedBy: string; markedAt: string; row: Row }> {
  if (!Array.isArray(raw)) return [];
  const out: Array<{ check: Id; markedBy: string; markedAt: string; row: Row }> = [];
  for (const entry of raw) {
    const e = entry as { check?: unknown; markedBy?: unknown; markedAt?: unknown; row?: unknown } | null;
    if (!e || typeof e !== 'object' || !ids.includes(e.check as Id) || !e.row || typeof e.row !== 'object') continue;
    out.push({
      check: e.check as Id,
      markedBy: typeof e.markedBy === 'string' ? e.markedBy : '',
      markedAt: typeof e.markedAt === 'string' ? e.markedAt : '',
      row: e.row as Row,
    });
  }
  return out;
}

/** The stores answer, or null when it is not one. */
export function readStores(raw: unknown): DataHygieneStores | null {
  const data = raw as Partial<DataHygieneStores> | null | undefined;
  const ids = STORE_CHECKS.map((c) => c.id);
  const checks = readChecks<StoreCheckId, HygieneStoreRow>(data?.checks, ids);
  if (!data || !checks) return null;
  return {
    asOf: typeof data.asOf === 'string' ? data.asOf : '',
    storesChecked: numberOr(data.storesChecked, 0),
    storesWithIssues: numberOr(data.storesWithIssues, distinctCount(checks, (r) => r.storeId)),
    checks,
    marked: readMarked<StoreCheckId, HygieneStoreRow>(data.marked, ids),
  };
}

/** The employees answer, or null when it is not one. */
export function readEmployees(raw: unknown): DataHygieneEmployees | null {
  const data = raw as Partial<DataHygieneEmployees> | null | undefined;
  const ids = EMPLOYEE_CHECKS.map((c) => c.id);
  const checks = readChecks<EmployeeCheckId, HygieneEmployeeRow>(data?.checks, ids);
  if (!data || !checks) return null;
  return {
    asOf: typeof data.asOf === 'string' ? data.asOf : '',
    peopleChecked: numberOr(data.peopleChecked, null),
    peopleWithIssues: numberOr(data.peopleWithIssues, distinctCount(checks, (r) => r.employeeCode)),
    checks,
    marked: readMarked<EmployeeCheckId, HygieneEmployeeRow>(data.marked, ids),
  };
}

function distinctCount<Row>(checks: Array<{ rows: Row[] }>, keyOf: (row: Row) => string): number {
  const seen = new Set<string>();
  for (const check of checks) for (const row of check.rows) seen.add(keyOf(row));
  return seen.size;
}

// ── The Role filter ──────────────────────────────────────────────────────────
// The dashboard's Filters › Roles (the page's shared Role filter, which opens on the
// roles that work at a store) narrows the EMPLOYEES side only, in the browser: every
// employee row already names the person's roles. The store checks are about the store
// records themselves (a name, a code, coordinates, a status), so no role reaches them.
// "Closed, people posted" is one of those: a closed store with anyone still posted at it
// is wrong whatever their role, and its row is the store.
//
// "No role" is never narrowed. Its people hold no role, so no role can match them, and a
// filter would always empty it. It lists everyone, whatever roles are chosen.

const roleKey = (name: string | null | undefined): string => String(name ?? '').trim().toLowerCase();

/**
 * Whether a person holds one of the wanted roles (trimmed, lower-case names).
 *
 * A "Two roles" row carries each role in `roles`. Every other row names them only in its
 * Role column, joined with ', ' as the API joins them. That text is matched a whole name
 * at a time, never split, so a role whose own name holds a comma is still found.
 */
export function holdsRole(row: Pick<HygieneEmployeeRow, 'roleName' | 'roles'>, wanted: ReadonlySet<string>): boolean {
  if (row.roles && row.roles.length > 0) return row.roles.some((role) => wanted.has(roleKey(role)));
  const joined = roleKey(row.roleName);
  if (!joined) return false;
  const padded = `, ${joined}, `;
  for (const role of wanted) if (padded.includes(`, ${role}, `)) return true;
  return false;
}

/**
 * The employees answer as the Role filter shows it: each check's rows, count and field
 * chips narrowed to people who hold one of `roles`, and the Employees count worked out
 * again from those rows. The Marked OK list is narrowed the same way. No role chosen
 * (the filter is empty: everyone) gives the answer back as it came. "No role", and a
 * check that could not be run, are left as they are.
 */
export function employeesInRoles(data: DataHygieneEmployees, roles: readonly string[]): DataHygieneEmployees {
  const wanted = new Set(roles.map(roleKey).filter(Boolean));
  if (wanted.size === 0) return data;
  // Someone with no role on record cannot match a role, so, as under "No role", they
  // are never filtered away: a role filter would hide them from every list.
  const keeps = (row: HygieneEmployeeRow): boolean => (!roleKey(row.roleName) && !(row.roles && row.roles.length)) || holdsRole(row, wanted);
  const checks = data.checks.map((check): HygieneEmployeeCheck => {
    if (check.status !== 'ready' || check.id === 'no-role') return check;
    const rows = check.rows.filter(keeps);
    // The chips keep the API's order and, as there, only the labels someone still misses.
    const fieldCounts = check.fieldCounts
      ?.map((f) => ({ label: f.label, count: rows.filter((row) => (row.missing ?? []).includes(f.label)).length }))
      .filter((f) => f.count > 0);
    return { ...check, count: rows.length, rows, fieldCounts };
  });
  // A person in three checks is one person to fix, as the API counts them.
  const people = new Set<string>();
  for (const check of checks) {
    if (check.status !== 'ready') continue;
    for (const row of check.rows) people.add(roleKey(row.employeeCode));
  }
  const marked = data.marked.filter((entry) => entry.check === 'no-role' || keeps(entry.row));
  return { ...data, checks, peopleWithIssues: people.size, marked };
}

/**
 * The few words the tab shows while a role is chosen: which figures the Role filter
 * narrows on the side that is open. The roles and the reason are its hover text. null
 * when no role is chosen: nothing is narrowed, so there is nothing to say.
 */
export function roleFilterNote(side: HygieneSide, roles: readonly string[]): { text: string; title: string } | null {
  const names = [...new Set(roles.map((r) => r.trim()).filter(Boolean))];
  if (names.length === 0) return null;
  if (side === 'stores') {
    return {
      text: 'Role filter: Employees only',
      title: 'The store checks are about the store records, so the Role filter leaves them as they are. It narrows the Employees side.',
    };
  }
  return {
    text: `Role filter: ${names.length <= 2 ? names.join(', ') : `${names.length} roles`} · except No role`,
    title: `The counts and lists show only people in ${names.join(', ')}. No role is not narrowed: those people hold no role, so it lists all of them.`,
  };
}

// ── Which check is open ──────────────────────────────────────────────────────

/**
 * The check whose list is drawn on a side: the one the address names when it belongs
 * to this side and has something in it, otherwise the side's first check that has.
 * null when the side has nothing to list.
 */
export function shownCheck<Info extends HygieneCheckInfo>(
  infos: readonly Info[],
  checks: ReadonlyArray<{ id: string; count: number | null }>,
  inAddress: HygieneCheckInfo | null,
): Info | null {
  const has = (info: Info) => (checks.find((c) => c.id === info.id)?.count ?? 0) > 0;
  const named = inAddress ? infos.find((i) => i.slug === inAddress.slug) : undefined;
  if (named && has(named)) return named;
  return infos.find(has) ?? null;
}

/**
 * What a side says in place of a list when no check has anything in it. "Nothing to
 * fix" is only said when every check ran: a check that could not be run may be hiding
 * rows.
 */
export function nothingToFixLine(side: HygieneSide, checks: ReadonlyArray<{ status: HygieneCheckStatus }>): string {
  if (checks.some((c) => c.status !== 'ready')) return 'Some checks could not be run just now. Press Refresh to try again.';
  return `Nothing to fix in ${side === 'stores' ? 'stores' : 'employees'}.`;
}

// ── Words ────────────────────────────────────────────────────────────────────

export const fmtInt = (n: number): string => n.toLocaleString('en-IN');

const plural = (n: number, one: string, many: string): string => `${fmtInt(n)} ${n === 1 ? one : many}`;

// What the stores of a duplicate check share, said after their count.
const SHARED: Partial<Record<StoreCheckId, (stores: number, groups: number) => string>> = {
  'same-name': (stores, groups) => `${stores === 1 ? 'shares' : 'share'} ${plural(groups, 'name', 'names')}`,
  'same-coordinates': (_stores, groups) => `at ${plural(groups, 'place', 'places')}`,
  'same-store-code': (stores, groups) => `${stores === 1 ? 'shares' : 'share'} ${plural(groups, 'store code', 'store codes')}`,
};

/**
 * The grey line under a list's name: its count in plain words. '96 stores',
 * '12 stores share 5 names', '9 stores at 4 places', '14 people'.
 *
 * `matching` is how many rows the search and the chip leave. When that is fewer than
 * the list holds, the line says so instead ('3 of 96 stores').
 */
export function countLine(info: HygieneCheckInfo, check: { count: number | null; groups?: number }, matching?: number): string {
  const side = HYGIENE_SIDES.find((s) => s.id === info.side) ?? HYGIENE_SIDES[0];
  const total = check.count ?? 0;
  const things = plural(total, side.one, side.many);
  if (matching !== undefined && matching !== total) return `${fmtInt(matching)} of ${things}`;
  const shared = info.side === 'stores' ? SHARED[info.id] : undefined;
  return shared && check.groups !== undefined ? `${things} ${shared(total, check.groups)}` : things;
}

/** 'Checked 3:05 pm' takes this: the API's `asOf` as a local clock time, null when it is not a time. */
export function fmtCheckedTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  return at.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' });
}

// ── What is wrong with a row ─────────────────────────────────────────────────

/** The checks whose list has a Status column: the ones where the status is part of what is wrong. */
export const showsStatus = (id: StoreCheckId): boolean =>
  id === 'same-store-code' || id === 'status-not-recognised' || id === 'closed-with-people';

const SAME: Partial<Record<StoreCheckId, string>> = {
  'same-name': 'Same name',
  'same-coordinates': 'Same coordinates',
  'same-store-code': 'Same store code',
};

export const isDuplicateCheck = (id: StoreCheckId): boolean => id in SAME;

// A check that can only miss one thing. The API names it on each row (`missing`); this
// is the same word, for a row that arrives without it, so the cell is never empty.
const ONE_THING_MISSING: Partial<Record<StoreCheckId, string>> = {
  'staffing-status-not-set': 'Staffing status',
};

/** The "What is wrong" cell of a store row. */
export function storeWrongText(id: StoreCheckId, row: HygieneStoreRow): string {
  if (id === 'closed-with-people' && row.people?.length) {
    return row.people.map((p) => p.employeeName.trim() || p.employeeCode).join(', ');
  }
  if (row.missing?.length) return row.missing.join(', ');
  if (row.problem) return row.problem;
  return SAME[id] ?? ONE_THING_MISSING[id] ?? '';
}

/** A store as one piece of text: its name, with the code in brackets when it has one. */
const storeText = (s: { storeName: string; storeCode: string }): string =>
  (s.storeCode ? `${s.storeName} (${s.storeCode})` : s.storeName);

/** The "What is wrong" cell of an employee row. */
export function employeeWrongText(id: EmployeeCheckId, row: HygieneEmployeeRow): string {
  switch (id) {
    case 'two-stores': return (row.stores ?? []).map(storeText).join(', ');
    // The Role column beside this cell already names the roles; this one says how many.
    // `roles` has one entry per role row, so the same role can be in it more than once:
    // the count is of different roles, as the Role column lists them.
    case 'two-roles': {
      const roles = row.roles ?? [];
      const different = new Set(roles).size;
      if (different === 0) return 'Two roles';
      if (different === 1) return roles.length === 2 ? 'Same role given twice' : `Same role given ${roles.length} times`;
      return `Holds ${different} roles`;
    }
    case 'no-role': return 'No role';
    case 'extra-field-missing': return (row.missing ?? []).join(', ');
    default: return '';
  }
}

// ── Search and chips ─────────────────────────────────────────────────────────

/** Every typed word must be somewhere in the row's text. Case does not matter. */
const matches = (parts: Array<string | undefined>, query: string): boolean => {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const text = parts.filter(Boolean).join(' ').toLowerCase();
  return words.every((w) => text.includes(w));
};

/** Search reads what the row shows: name, code, category, city, state, its block's heading and what is wrong. */
export const storeMatchesSearch = (id: StoreCheckId, row: HygieneStoreRow, query: string): boolean =>
  matches([row.storeName, row.storeCode, row.category, row.city, row.state, row.groupLabel, storeWrongText(id, row)], query);

export const employeeMatchesSearch = (id: EmployeeCheckId, row: HygieneEmployeeRow, query: string): boolean =>
  matches([row.employeeName, row.employeeCode, row.roleName, employeeWrongText(id, row)], query);

/** A chip narrows the rows to those missing that one field. No chip = every row. */
export const missesField = (row: { missing?: string[] }, label: string | null): boolean =>
  label === null || (row.missing ?? []).includes(label);

/** The chip that is on, as long as the list still has it: a Refresh can take a label away. */
export const activeChip = (chip: string | null, fieldCounts: HygieneFieldCount[] | undefined): string | null =>
  (chip !== null && (fieldCounts ?? []).some((f) => f.label === chip) ? chip : null);

// ── Blocks of a duplicate check ──────────────────────────────────────────────

export interface StoreBlock {
  key: string;
  /** The shared name, point or code. */
  label: string;
  /** same-name: at least two stores of the block are in one city. */
  sameCity: boolean;
  /** How many stores share it in the whole list, whatever the search has hidden. */
  size: number;
  rows: HygieneStoreRow[];
}

// A row with no groupKey stands alone, under its own name.
const blockKey = (row: HygieneStoreRow): string => row.groupKey ?? `store:${row.storeId}`;

/**
 * Rows gathered into the blocks they belong to, in the order each block first appears.
 * `all` is the whole list: a block's heading counts every store that shares the value,
 * so it stays true while the search hides some of them.
 */
export function storeBlocks(rows: HygieneStoreRow[], all: HygieneStoreRow[] = rows): StoreBlock[] {
  const sizes = new Map<string, number>();
  for (const row of all) sizes.set(blockKey(row), (sizes.get(blockKey(row)) ?? 0) + 1);
  const blocks = new Map<string, StoreBlock>();
  for (const row of rows) {
    const key = blockKey(row);
    let block = blocks.get(key);
    if (!block) {
      block = { key, label: row.groupLabel ?? row.storeName, sameCity: false, size: sizes.get(key) ?? 0, rows: [] };
      blocks.set(key, block);
    }
    if (row.sameCity) block.sameCity = true;
    block.rows.push(row);
  }
  return [...blocks.values()];
}

/** A block's heading: 'Sample Name · 3 stores'. */
export const blockHeading = (block: StoreBlock): string => `${block.label} · ${plural(block.size, 'store', 'stores')}`;

/** How many rows a long list draws before 'Show all'. */
export const ROW_LIMIT = 100;

/**
 * The blocks that hold the first `limit` rows. The last one is cut short when it does
 * not fit: one name on five hundred stores must not draw five hundred rows at once.
 * Its heading still counts every store that shares the value.
 */
export function firstBlocks(blocks: StoreBlock[], limit: number = ROW_LIMIT): StoreBlock[] {
  const out: StoreBlock[] = [];
  let left = limit;
  for (const block of blocks) {
    if (left <= 0) break;
    out.push(block.rows.length <= left ? block : { ...block, rows: block.rows.slice(0, left) });
    left -= block.rows.length;
  }
  return out;
}

// ── Where a row is fixed ─────────────────────────────────────────────────────
// The three pages read ?q= into their search box, so the link lands on the record.

export const STORES_PAGE = '/master-management/stores';
export const EMPLOYEES_PAGE = '/master-management/employees';
export const USER_ROLES_PAGE = '/settings/user-roles';

/** The Stores page, searched for this store: by its code, or by its name when it has none. */
export const storeFixLink = (row: Pick<HygieneStoreRow, 'storeCode' | 'storeName'>): string =>
  `${STORES_PAGE}?q=${encodeURIComponent(row.storeCode.trim() || row.storeName.trim())}`;

/** A role is given and taken on User Role Assignment; everything else about a person is on Employees. */
export const employeeFixPage = (id: EmployeeCheckId): string =>
  (id === 'two-roles' || id === 'no-role' ? USER_ROLES_PAGE : EMPLOYEES_PAGE);

// User Role Assignment lists only people who already hold a role, so a search for
// someone with none would open on an empty list. Their link opens the page whole; the
// role is given from its "Add & Assign Role".
export const employeeFixLink = (id: EmployeeCheckId, row: Pick<HygieneEmployeeRow, 'employeeCode'>): string =>
  (id === 'no-role' ? USER_ROLES_PAGE : `${employeeFixPage(id)}?q=${encodeURIComponent(row.employeeCode.trim())}`);

// ── The Excel sheet ──────────────────────────────────────────────────────────
// The rows as the list shows them. A cell that holds two things on screen (name over
// code, category over city) is two columns here, so each can be sorted and filtered.

// A duplicate check's blocks have no heading row in a sheet. Their shared value is
// the first column instead.
const SHARED_COLUMN: Partial<Record<StoreCheckId, string>> = {
  'same-name': 'Shared name',
  'same-coordinates': 'Shared coordinates',
  'same-store-code': 'Shared store code',
};

export function storeSheet(info: StoreCheckInfo, rows: HygieneStoreRow[]): TableSheet {
  const shared = SHARED_COLUMN[info.id];
  const status = showsStatus(info.id);
  // As on screen: a duplicate list has no "What is wrong" column. Its first column
  // already says what the stores share.
  const wrong = !shared;
  const headers = [
    ...(shared ? [shared] : []),
    'Store', 'Store Code', 'Category', 'City', 'State',
    ...(wrong ? ['What is wrong'] : []),
    ...(status ? ['Status'] : []),
  ];
  const body = rows.map((row): TableCell[] => [
    ...(shared ? [row.groupLabel ?? ''] : []),
    row.storeName, row.storeCode, row.category, row.city, row.state,
    ...(wrong ? [storeWrongText(info.id, row)] : []),
    ...(status ? [row.status] : []),
  ]);
  return { name: info.label, headers, rows: body };
}

export function employeeSheet(info: EmployeeCheckInfo, rows: HygieneEmployeeRow[]): TableSheet {
  return {
    name: info.label,
    headers: ['Employee', 'Employee Code', 'Role', 'What is wrong'],
    rows: rows.map((row): TableCell[] => [row.employeeName, row.employeeCode, row.roleName, employeeWrongText(info.id, row)]),
  };
}

/** 'data-hygiene-same-name-2026-10-13': the check and the day in India it was saved. */
export const hygieneFileName = (info: HygieneCheckInfo, now: Date = new Date()): string =>
  `data-hygiene-${info.slug}-${now.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' })}`;
